/**
 * intakeMap (Seth 2026-06-26) — turn the AI intake fields (summarize-intake's
 * `fields`) into the dispatch builder's actual control values, so a web form /
 * recording / chat fills Urgency + Arrival window, not just the notes box.
 * "Work as much of the available info into the dispatch builder as possible."
 * Anything we can't confidently map returns null → the builder keeps its default.
 */

export type BuilderUrgency = "asap" | "soon" | "routine";

/** AI urgency (emergency|asap|soon|routine) → the builder's 3 levels. */
export function mapUrgency(ai: string | null | undefined): BuilderUrgency | null {
  switch ((ai ?? "").toLowerCase().trim()) {
    case "emergency":
    case "asap":
    case "immediately":
    case "urgent":
      return "asap";
    case "soon":
      return "soon";
    case "routine":
    case "flexible":
    case "whenever":
      return "routine";
    default:
      return null;
  }
}

/** Free-text window ("8am – 12pm", "noon to 4", "first call") → a WINDOWS code
 *  (8AM | 8-12 | 10-2 | 12-4 | 3-7). null when nothing clear is stated. */
export function mapWindow(ai: string | null | undefined): string | null {
  const s = (ai ?? "").toLowerCase();
  if (!s.trim()) return null;
  // bounded single-digit test so "2" doesn't match inside "12"
  const hr = (n: number) => new RegExp(`(^|[^\\d])${n}(\\b|am|pm|:00|[^\\d]|$)`).test(s);
  const noon = /\bnoon\b/.test(s);
  if (hr(8) && (hr(12) || noon)) return "8-12";
  if (hr(10) && hr(2)) return "10-2";
  if ((hr(12) || noon) && hr(4)) return "12-4";
  if (hr(3) && hr(7)) return "3-7";
  if (/first call|first thing|early|8\s*am/.test(s) || hr(8)) return "8AM";
  return null;
}

/** A self-contained web-form request (vs a chatty back-and-forth): comes in as
 *  one complete message of "Label: value" lines. These get a per-bubble "Make a
 *  dispatch" button (like a voice recording), not the conversation summarizer. */
export function looksLikeWebForm(body: string | null | undefined): boolean {
  const s = body ?? "";
  if (!s.trim()) return false;
  if (/appointment request|service request|web (form|request)|request a quote/i.test(s)) return true;
  const labelLines = (s.match(/^[A-Z][A-Za-z /&]{2,40}:\s+\S/gm) || []).length;
  return labelLines >= 4;
}
